import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Twitter, Github, Mail, Loader2 } from "lucide-react";
import Logo from "./Logo";
import { subscribeNewsletter } from "../lib/api";

const COLS = [
    {
        title: "Services",
        links: [
            { label: "Web Audits", to: "/services#web-audits" },
            { label: "PDF Remediation", to: "/services#pdf-remediation" },
            { label: "Design Audits", to: "/services#design-audits" },
            { label: "VPAT/ACR", to: "/services#vpat-acr" },
        ],
    },
    {
        title: "Company",
        links: [
            { label: "About", to: "/about" },
            { label: "Blog", to: "/resources#blog" },
            { label: "Contact", to: "/contact" },
            { label: "Careers", to: "/about#careers" },
        ],
    },
    {
        title: "Compliance",
        links: [
            { label: "WCAG", to: "/resources#wcag" },
            { label: "ADA", to: "/resources#ada" },
            { label: "EAA", to: "/resources#eaa" },
            { label: "Section 508", to: "/resources#section-508" },
            { label: "AODA", to: "/resources#aoda" },
        ],
    },
    {
        title: "Resources",
        links: [
            { label: "Free Audit", to: "/free-audit" },
            { label: "Accessibility Statement", to: "/accessibility-statement" },
            { label: "Privacy", to: "/privacy" },
            { label: "Terms", to: "/terms" },
        ],
    },
];

export const Footer = () => {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState({ state: "idle", msg: "" });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
            setStatus({ state: "error", msg: "Please enter a valid email address." });
            return;
        }
        setStatus({ state: "loading", msg: "" });
        try {
            await subscribeNewsletter(email);
            setStatus({ state: "success", msg: "Thanks — you're subscribed." });
            setEmail("");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setStatus({
                state: "error",
                msg: detail || "Something went wrong. Please try again.",
            });
        }
    };

    return (
        <footer
            className="bg-brand-navy text-white"
            role="contentinfo"
            data-testid="site-footer"
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-4">
                        <div className="bg-white rounded-lg p-4 inline-block shadow-sm">
                            <Logo className="h-10 w-auto" />
                        </div>
                        <p className="mt-5 text-sm text-white/75 max-w-xs leading-relaxed">
                            Accessibility audits, document remediation, and design reviews —
                            engineered for global compliance.
                        </p>

                        <form
                            onSubmit={onSubmit}
                            className="mt-8 max-w-sm"
                            aria-labelledby="newsletter-heading"
                            data-testid="newsletter-form"
                        >
                            <h2 id="newsletter-heading" className="text-white text-sm font-semibold uppercase tracking-wider">
                                Newsletter
                            </h2>
                            <p className="text-xs text-white/70 mt-1 mb-3">
                                Quarterly accessibility insights. No spam.
                            </p>
                            <label htmlFor="newsletter-email" className="sr-only">Email address</label>
                            <div className="flex gap-2">
                                <input
                                    id="newsletter-email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@company.com"
                                    aria-describedby="newsletter-status"
                                    className="flex-1 bg-white/10 border border-white/20 text-white placeholder-white/50 rounded-md px-3 py-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5EE6DC]"
                                    data-testid="newsletter-email-input"
                                />
                                <button
                                    type="submit"
                                    disabled={status.state === "loading"}
                                    className="inline-flex items-center justify-center rounded-md bg-[#1A8B82] hover:bg-[#23a89e] text-white font-semibold px-4 py-3 text-sm transition-colors disabled:opacity-60"
                                    data-testid="newsletter-submit-button"
                                >
                                    {status.state === "loading" ? (
                                        <Loader2 size={16} className="animate-spin" aria-hidden />
                                    ) : (
                                        "Subscribe"
                                    )}
                                </button>
                            </div>
                            <p
                                id="newsletter-status"
                                aria-live="polite"
                                role="status"
                                className={`mt-2 text-xs min-h-[1rem] ${
                                    status.state === "error" ? "text-[#FCA5A5]" :
                                    status.state === "success" ? "text-[#86EFAC]" : "text-white/60"
                                }`}
                                data-testid="newsletter-status"
                            >
                                {status.msg}
                            </p>
                        </form>
                    </div>

                    {COLS.map((col) => (
                        <nav
                            key={col.title}
                            aria-label={col.title}
                            className="md:col-span-2"
                        >
                            <h2 className="text-white text-sm font-semibold uppercase tracking-wider mb-4">
                                {col.title}
                            </h2>
                            <ul className="space-y-3">
                                {col.links.map((l) => (
                                    <li key={l.label}>
                                        <Link
                                            to={l.to}
                                            className="text-sm text-white/75 hover:text-white hover:underline transition-colors"
                                            data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                        >
                                            {l.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    ))}
                </div>

                <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <p className="text-xs text-white/60">
                        © {new Date().getFullYear()} AccessNova. All rights reserved.
                    </p>
                    <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
                        <li><Link to="/privacy" className="text-white/70 hover:text-white">Privacy</Link></li>
                        <li><Link to="/terms" className="text-white/70 hover:text-white">Terms</Link></li>
                        <li><Link to="/accessibility-statement" className="text-white/70 hover:text-white">Accessibility Statement</Link></li>
                        <li><Link to="/sitemap" className="text-white/70 hover:text-white">Sitemap</Link></li>
                    </ul>
                    <div className="flex items-center gap-4">
                        <a href="https://www.linkedin.com/company/accessnova/" target="_blank" rel="noopener noreferrer"
                           aria-label="AccessNova on LinkedIn"
                           className="text-white/75 hover:text-white touch-target inline-flex items-center justify-center"
                           data-testid="social-linkedin">
                            <Linkedin size={20} />
                        </a>
                        <a href="mailto:hello@accessnova.co"
                           aria-label="Email AccessNova"
                           className="text-white/75 hover:text-white touch-target inline-flex items-center justify-center"
                           data-testid="social-email">
                            <Mail size={20} />
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
